import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { useSiteSettings } from "@/lib/api";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    google_tag_manager?: Record<string, unknown>;
  }
}

function scheduleWhenIdle(fn: () => void, timeout = 3000) {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(fn, { timeout });
  } else {
    setTimeout(fn, 50);
  }
}

export function GTMScript() {
  const { data: settings } = useSiteSettings();
  const [location] = useLocation();
  const lastLocation = useRef<string | null>(null);
  const headScriptsInjected = useRef(false);
  const bodyScriptsInjected = useRef(false);

  const injectHeadScripts = useCallback((headScripts: string) => {
    const doc = new DOMParser().parseFromString(headScripts, 'text/html');
    const container = doc.body;

    const scripts = container.querySelectorAll('script');
    scripts.forEach((script) => {
      const newScript = document.createElement('script');
      Array.from(script.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = script.textContent;
      document.head.appendChild(newScript);
    });

    Array.from(container.children).forEach((child) => {
      if (child.tagName.toLowerCase() !== 'script') {
        document.head.appendChild(child.cloneNode(true));
      }
    });

    headScriptsInjected.current = true;
    console.log('Custom head scripts injected');
  }, []);

  const injectBodyScripts = useCallback((bodyScripts: string) => {
    const doc = new DOMParser().parseFromString(bodyScripts, 'text/html');
    const container = doc.body;

    const scripts = container.querySelectorAll('script');
    scripts.forEach((script) => {
      const newScript = document.createElement('script');
      Array.from(script.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = script.textContent;
      document.body.appendChild(newScript);
    });

    Array.from(container.children).forEach((child) => {
      if (child.tagName.toLowerCase() !== 'script') {
        document.body.appendChild(child.cloneNode(true));
      }
    });

    bodyScriptsInjected.current = true;
    console.log('Custom body scripts injected');
  }, []);

  useEffect(() => {
    const headScripts = settings?.headScripts?.trim();
    if (!headScripts || headScriptsInjected.current) return;
    scheduleWhenIdle(() => injectHeadScripts(headScripts));
  }, [settings?.headScripts, injectHeadScripts]);

  useEffect(() => {
    const bodyScripts = settings?.bodyScripts?.trim();
    if (!bodyScripts || bodyScriptsInjected.current) return;
    scheduleWhenIdle(() => injectBodyScripts(bodyScripts));
  }, [settings?.bodyScripts, injectBodyScripts]);

  useEffect(() => {
    if (lastLocation.current === location) return;
    lastLocation.current = location;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'virtualPageview',
      page: location,
      pageTitle: document.title,
      pageUrl: window.location.href
    });
    console.log('GTM pageview fired:', location);
  }, [location]);

  useEffect(() => {
    const siteTitle = settings?.siteTitle?.trim();
    const siteDescription = settings?.siteDescription?.trim();
    
    if (siteTitle && location === '/') {
      document.title = siteTitle.includes('Top Breakdown Cover') ? siteTitle : `${siteTitle} - Top Breakdown Cover`;
    }
    
    if (siteDescription) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', siteDescription);
      }
    }
  }, [settings?.siteTitle, settings?.siteDescription, location]);

  return null;
}
